import React, { useContext, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { motion } from "framer-motion";
import { Target, Zap, Clock, AlertTriangle, Copy, Check, ExternalLink } from "lucide-react";
import { Badge } from "../GraderUI";
import { getSeverityClass } from "../gradeColors";
import { ProductContext } from "../../../store";
import useWritingPreviewAction from "../WritingAssistance/useWritingPreviewAction";
import {
	normalizePageUrl,
	normalizeRecommendationId,
} from "../WritingAssistance/writingAssistantUtils";
import { formatCategoryLabel } from "../categoryLabels";

const quadrantConfig = {
	quick_wins: {
		title: "Quick Wins",
		subtitle: "High Impact, Low Effort",
		icon: Zap,
		modifier: "quick-wins",
	},
	major_projects: {
		title: "Major Projects",
		subtitle: "High Impact, High Effort",
		icon: Target,
		modifier: "major-projects",
	},
	fill_ins: {
		title: "Fill-Ins",
		subtitle: "Low Impact, Low Effort",
		icon: Clock,
		modifier: "fill-ins",
	},
	time_wasters: {
		title: "Time Wasters",
		subtitle: "Low Impact, High Effort",
		icon: AlertTriangle,
		modifier: "time-wasters",
	},
};

const TAG_MODIFIERS = {
	faq_readiness: "faq",
	brand_entity: "brand",
	schema: "schema",
	answerability: "answerability",
	trust_signals: "trust",
	readability: "readability",
	internal_linking: "linking",
	ai_citation: "citation",
	ai_discoverability: "discoverability",
	e_clarity: "clarity",
	technical: "technical",
	content_depth: "content",
	freshness: "freshness",
	multimodal: "multimodal",
	experience: "trust",
	expertise: "trust",
	llms_txt: "discoverability",
};

const getImpactClass = (impact) =>
	`action-matrix__impact-badge action-matrix__impact-badge--${impact}`;
const getEffortClass = (effort) =>
	`action-matrix__effort-badge action-matrix__effort-badge--${effort}`;

const getPriorityBarClass = (rankPercent) => {
	if (rankPercent > 66) return "action-matrix__priority-bar-fill--high";
	if (rankPercent > 33) return "action-matrix__priority-bar-fill--medium";
	return "action-matrix__priority-bar-fill--low";
};

const isTruncated = (text) => text && !/[.!?`\/]$/.test(text.trimEnd());

function handleMatrixItemKeyDown(event, onActivate) {
	if (event.key === "Enter" || event.key === " ") {
		event.preventDefault();
		onActivate();
	}
}

function formatExampleCode(code) {
	if (!code) return "";
	try {
		return JSON.stringify(JSON.parse(code), null, 2);
	} catch {
		return code;
	}
}

export default function ActionMatrixSection({
	data,
	auditId,
	title = "Prioritized Action Matrix",
	subtitle = "Strategic actions organized by impact and effort",
	onGenerateLlmsTxt,
	llmsTxtSubmitting = false,
}) {
	const { currentWebsiteId } = useContext(ProductContext);
	const {
		canPreviewAction,
		getPreviewActionState,
		submitOrViewPreview,
		submittingKey,
		isReady,
	} = useWritingPreviewAction({ websiteId: currentWebsiteId, auditId });

	const [expandedAction, setExpandedAction] = useState(null);
	const [selectedQuadrant, setSelectedQuadrant] = useState("all");
	const [copiedCode, setCopiedCode] = useState(null);
	const [showAllPages, setShowAllPages] = useState({});

	const copyToClipboard = async (code, actionId) => {
		try {
			await navigator.clipboard.writeText(code);
			setCopiedCode(actionId);
			setTimeout(() => setCopiedCode(null), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	if (!data) {
		return null;
	}

	const { statistics } = data;

	const getActionsToDisplay = () => {
		if (selectedQuadrant === "all") {
			return data.all_actions;
		}
		return data[selectedQuadrant] || [];
	};

	const actionsToDisplay = getActionsToDisplay();

	return (
		<motion.div
			className="action-matrix"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
		>
			<div className="action-matrix__header">
				<div className="action-matrix__header-icon-wrap">
					<Target className="action-matrix__header-icon" />
				</div>
				<div>
					<h2 className="action-matrix__title">{title}</h2>
					<p className="action-matrix__subtitle">{subtitle}</p>
				</div>
			</div>

			<div className="action-matrix__stats">
				{Object.entries(quadrantConfig).map(([key, config]) => {
					const count = statistics[`${key}_count`];
					const Icon = config.icon;
					const isActive = selectedQuadrant === key;

					return (
						<button
							key={key}
							type="button"
							onClick={() => setSelectedQuadrant(key)}
							className={`action-matrix__stat-card action-matrix__stat-card--${config.modifier}${
								isActive ? " action-matrix__stat-card--active" : ""
							}`}
						>
							<div className="action-matrix__stat-header">
								<Icon className={`action-matrix__stat-icon action-matrix__stat-icon--${config.modifier}`} />
								<span className="action-matrix__stat-title">{config.title}</span>
							</div>
							<p className="action-matrix__stat-count">{count}</p>
							<p className="action-matrix__stat-subtitle">{config.subtitle}</p>
						</button>
					);
				})}
			</div>

			<div className="action-matrix__filter">
				<button
					type="button"
					onClick={() => setSelectedQuadrant("all")}
					className={`action-matrix__filter-btn${
						selectedQuadrant === "all" ? " action-matrix__filter-btn--active" : ""
					}`}
				>
					All Actions ({statistics.total_actions})
				</button>
			</div>

			<div className="action-matrix__list">
				{actionsToDisplay.length === 0 ? (
					<div className="action-matrix__empty">No actions in this category</div>
				) : (
					actionsToDisplay.map((action) => {
						const quadrant = quadrantConfig[action.quadrant];
						if (!quadrant) return null;
						const Icon = quadrant.icon;
						const category = action.category_tag || action.category;
						const tagModifier = TAG_MODIFIERS[category] || "default";

						return (
							<div
								key={action.id}
								className={`action-matrix__item action-matrix__item--${quadrant.modifier}`}
							>
								<div
									className={`action-matrix__item-head action-matrix__item-head--${quadrant.modifier}`}
								>
									<div
										role="button"
										tabIndex={0}
										onClick={() =>
											setExpandedAction(expandedAction === action.id ? null : action.id)
										}
										onKeyDown={(event) =>
											handleMatrixItemKeyDown(event, () =>
												setExpandedAction(
													expandedAction === action.id ? null : action.id
												)
											)
										}
										className={`action-matrix__item-trigger action-matrix__item-trigger--${quadrant.modifier}`}
									>
									<div className="action-matrix__item-main">
										<Icon
											className={`action-matrix__item-icon action-matrix__item-icon--${quadrant.modifier}`}
										/>
										<div className="action-matrix__item-content">
											<div className="action-matrix__item-title-row">
												<h4 className="action-matrix__item-title">{action.title}</h4>
												{category && (
													<Badge
														className={`action-matrix__tag action-matrix__tag--${tagModifier}`}
													>
														{formatCategoryLabel(category)}
													</Badge>
												)}
												{action.affected_pages && action.affected_pages.length > 0 && (
													<Badge variant="secondary" className="action-matrix__pages-badge">
														{action.affected_pages.length}{" "}
														{action.affected_pages.length === 1 ? "page" : "pages"}
													</Badge>
												)}
												{category === "llms_txt" && (
													<Button
														color="primary"
														size="sm"
														className="action-matrix__generate-btn"
														disabled={llmsTxtSubmitting || !onGenerateLlmsTxt}
														onClick={(event) => {
															event.stopPropagation();
															onGenerateLlmsTxt?.();
														}}
													>
														{llmsTxtSubmitting ? (
															<>
																<Spinner size="sm" className="me-2" />
																Starting…
															</>
														) : (
															"Generate llms.txt"
														)}
													</Button>
												)}
												{action.type === "issue" && action.severity && (
													<Badge className={getSeverityClass(action.severity)}>
														{action.severity}
													</Badge>
												)}
											</div>
											<div className="action-matrix__item-badges">
												<Badge className={getImpactClass(action.impact)}>
													Impact: {action.impact}
												</Badge>
												<Badge className={getEffortClass(action.effort)}>
													Effort: {action.effort}
												</Badge>
												{(() => {
													const rank =
														actionsToDisplay.findIndex((a) => a.id === action.id) + 1;
													const total = actionsToDisplay.length;
													const rankPercent = ((total - rank + 1) / total) * 100;

													return (
														<div className="action-matrix__priority">
															<span className="action-matrix__priority-label">
																Priority #{rank} of {total}
															</span>
															<div className="action-matrix__priority-bar">
																<div
																	className={`action-matrix__priority-bar-fill ${getPriorityBarClass(rankPercent)}`}
																	style={{ width: `${rankPercent}%` }}
																/>
															</div>
														</div>
													);
												})()}
											</div>
										</div>
									</div>
									<span className="action-matrix__item-chevron">
										{expandedAction === action.id ? "▼" : "▶"}
									</span>
								</div>
								</div>

								{expandedAction === action.id && (
									<div className="action-matrix__item-body">
										<div className="action-matrix__detail">
											<p className="action-matrix__detail-label">Description</p>
											<p className="action-matrix__detail-text">
												{action.description}
												{isTruncated(action.description) && (
													<span className="action-matrix__incomplete">
														[content may be incomplete]
													</span>
												)}
											</p>
										</div>

										{action.affected_pages && action.affected_pages.length > 0 && (
											<div className="action-matrix__detail">
												<p className="action-matrix__detail-label">Affected Pages</p>
												<div className="action-matrix__pages-list">
													{action.affected_pages
														.slice(0, showAllPages[action.id] ? undefined : 3)
														.map((page, idx) => (
															<div key={idx} className="action-matrix__page-row">
																<Badge variant="secondary" className="action-matrix__page-type">
																	{page.page_type}
																</Badge>
																<span className="action-matrix__page-title" title={page.title}>
																	{page.title}
																</span>
																<a
																	href={page.url}
																	target="_blank"
																	rel="noopener noreferrer"
																	onClick={(e) => e.stopPropagation()}
																	className="action-matrix__page-link"
																	title="Open in new tab"
																>
																	<ExternalLink className="action-matrix__page-link-icon" />
																</a>
																{isReady && canPreviewAction(action) && page.url && (() => {
																	const previewState = getPreviewActionState(
																		action.id,
																		page.url
																	);
																	const submitKey = `${normalizeRecommendationId(action.id)}|${normalizePageUrl(page.url)}`;
																	return (
																	<button
																		type="button"
																		className="action-matrix__page-preview-btn"
																		disabled={
																			!previewState.canSubmit ||
																			submittingKey === submitKey
																		}
																		onClick={(e) => {
																			e.stopPropagation();
																			submitOrViewPreview({
																				recommendationId: action.id,
																				url: page.url,
																			});
																		}}
																	>
																		{previewState.label}
																	</button>
																	);
																})()}
															</div>
														))}
													{action.affected_pages.length > 3 && (
														<button
															type="button"
															onClick={(e) => {
																e.stopPropagation();
																setShowAllPages((prev) => ({
																	...prev,
																	[action.id]: !prev[action.id],
																}));
															}}
															className="action-matrix__pages-toggle"
														>
															{showAllPages[action.id]
																? "Show less"
																: `+ ${action.affected_pages.length - 3} more`}
														</button>
													)}
												</div>
											</div>
										)}

										{action.why_it_matters && (
											<div className="action-matrix__detail">
												<p className="action-matrix__detail-label">Why It Matters</p>
												<p className="action-matrix__detail-text">
													{action.why_it_matters}
													{isTruncated(action.why_it_matters) && (
														<span className="action-matrix__incomplete">
															[content may be incomplete]
														</span>
													)}
												</p>
											</div>
										)}

										{action.recommended_fix && (
											<div className="action-matrix__detail">
												<p className="action-matrix__detail-label">Recommended Fix</p>
												<p className="action-matrix__detail-text action-matrix__detail-text--pre">
													{action.recommended_fix}
													{isTruncated(action.recommended_fix) && (
														<span className="action-matrix__incomplete">
															[content may be incomplete]
														</span>
													)}
												</p>
											</div>
										)}

										{action.example && (
											<div className="action-matrix__example">
												<p className="action-matrix__example-label">For example</p>
												<p className="action-matrix__example-text">{action.example}</p>
											</div>
										)}

										{action.sub_items && action.sub_items.length > 0 && (
											<div className="action-matrix__detail">
												<p className="action-matrix__detail-label">Also addresses:</p>
												<ul className="action-matrix__sub-items">
													{action.sub_items.map((item, idx) => (
														<li key={idx}>{item}</li>
													))}
												</ul>
											</div>
										)}

										{action.example_code && (
											<div className="action-matrix__detail">
												<p className="action-matrix__detail-label">Example Implementation</p>
												<div className="action-matrix__code-wrap">
													<button
														type="button"
														onClick={() =>
															copyToClipboard(
																formatExampleCode(action.example_code),
																action.id
															)
														}
														className="action-matrix__copy-btn"
														title="Copy to clipboard"
													>
														{copiedCode === action.id ? (
															<Check className="action-matrix__copy-icon action-matrix__copy-icon--success" />
														) : (
															<Copy className="action-matrix__copy-icon" />
														)}
													</button>
													<pre className="action-matrix__code">
														<code>{formatExampleCode(action.example_code)}</code>
													</pre>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						);
					})
				)}
			</div>

			<div className="action-matrix__guide">
				<h4 className="action-matrix__guide-title">How to Use This Matrix</h4>
				<div className="action-matrix__guide-grid">
					<div>
						<span className="action-matrix__guide-label">Quick Wins:</span> Start here! High
						impact with low effort.
					</div>
					<div>
						<span className="action-matrix__guide-label">Major Projects:</span> Plan these for
						long-term improvement.
					</div>
					<div>
						<span className="action-matrix__guide-label">Fill-Ins:</span> Do these when you have
						spare time.
					</div>
					<div>
						<span className="action-matrix__guide-label">Time Wasters:</span> Avoid or
						deprioritize these.
					</div>
				</div>
			</div>
		</motion.div>
	);
}
